import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useEntitlements } from '../store/EntitlementStore';
import './AccessRequestsPage.css';

/**
 * Access Requests page - Super Admin dashboard for reviewing access requests.
 */
export const AccessRequestsPage = () => {
  const { user } = useEntitlements();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Check if user is super admin
  const isSuperAdmin = user?.is_superuser;

  useEffect(() => {
    if (!isSuperAdmin) {
      setError('Access denied. Super Admin privileges required.');
      setLoading(false);
      return;
    }
    fetchRequests();
  }, [filter, isSuperAdmin]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await api.get('/v1/access-requests/', { params });
      setRequests(response.data.results || response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load access requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this access request?')) {
      return;
    }

    try {
      setActionLoading(true);
      await api.post(`/v1/access-requests/${requestId}/approve/`, {
        notes: reviewNotes,
      });
      setSelectedRequest(null);
      setReviewNotes('');
      await fetchRequests();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Are you sure you want to reject this access request?')) {
      return;
    }

    try {
      setActionLoading(true);
      await api.post(`/v1/access-requests/${requestId}/reject/`, {
        notes: reviewNotes,
      });
      setSelectedRequest(null);
      setReviewNotes('');
      await fetchRequests();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      default:
        return '';
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="access-requests-page">
        <div className="error-message">
          <h2>Access Denied</h2>
          <p>Super Admin privileges are required to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="access-requests-page">
      <div className="page-header">
        <h1>Access Requests</h1>
        <p>Review and manage tenant access requests</p>
      </div>

      <div className="filters">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          className={filter === 'pending' ? 'active' : ''}
          onClick={() => setFilter('pending')}
        >
          Pending
        </button>
        <button
          className={filter === 'approved' ? 'active' : ''}
          onClick={() => setFilter('approved')}
        >
          Approved
        </button>
        <button
          className={filter === 'rejected' ? 'active' : ''}
          onClick={() => setFilter('rejected')}
        >
          Rejected
        </button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading access requests...</div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <p>No access requests found.</p>
        </div>
      ) : (
        <div className="requests-table">
          <table>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Requested</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td data-label="Organization">{request.organization_name}</td>
                  <td data-label="Contact">{request.primary_contact_name}</td>
                  <td data-label="Email">{request.email}</td>
                  <td data-label="Requested">{formatDate(request.created_at)}</td>
                  <td data-label="Status">
                    <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                      {request.status_display || request.status}
                    </span>
                  </td>
                  <td data-label="Actions">
                    <button
                      className="btn-view"
                      onClick={() => setSelectedRequest(request)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedRequest && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Access Request Details</h2>
              <button
                className="btn-close"
                onClick={() => setSelectedRequest(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Organization Information</h3>
                <p><strong>Name:</strong> {selectedRequest.organization_name}</p>
                <p><strong>Contact:</strong> {selectedRequest.primary_contact_name}</p>
                <p><strong>Email:</strong> {selectedRequest.email}</p>
                <p><strong>Phone:</strong> {selectedRequest.phone || 'Not provided'}</p>
              </div>

              <div className="detail-section">
                <h3>Request Details</h3>
                <p><strong>How do you plan to use Portalr?</strong></p>
                <p className="intended-use">{selectedRequest.intended_use || 'Not provided'}</p>
                {selectedRequest.expected_clients && (
                  <p><strong>Expected Clients:</strong> {selectedRequest.expected_clients}</p>
                )}
                {selectedRequest.expected_staff && (
                  <p><strong>Expected Staff:</strong> {selectedRequest.expected_staff}</p>
                )}
              </div>

              <div className="detail-section">
                <h3>Status</h3>
                <p><strong>Status:</strong> {selectedRequest.status_display || selectedRequest.status}</p>
                <p><strong>Requested:</strong> {formatDate(selectedRequest.created_at)}</p>
                {selectedRequest.reviewed_at && (
                  <p><strong>Reviewed:</strong> {formatDate(selectedRequest.reviewed_at)}</p>
                )}
                {selectedRequest.reviewed_by_email && (
                  <p><strong>Reviewed By:</strong> {selectedRequest.reviewed_by_email}</p>
                )}
                {selectedRequest.review_notes && (
                  <div>
                    <p><strong>Review Notes:</strong></p>
                    <p className="review-notes">{selectedRequest.review_notes}</p>
                  </div>
                )}
              </div>

              {selectedRequest.status === 'pending' && (
                <div className="detail-section">
                  <h3>Review Notes</h3>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Optional notes for this review..."
                    rows={4}
                  />
                </div>
              )}

              {selectedRequest.status === 'pending' && (
                <div className="modal-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(selectedRequest.id)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : 'Approve'}
                  </button>
                  <button
                    className="btn-reject"
                    onClick={() => handleReject(selectedRequest.id)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : 'Reject'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
